import { API_ENDPOINT } from '../services/api';
import { getScaledValue } from '../ui/styled';
import branding from '../../config/branding';

/**
 * Raw Strapi entity — may be Strapi 4 (nested `attributes` / `data`) or Strapi 5
 * (flat: attributes hoisted to the entity root, `documentId` added, media as
 * `{ url }` instead of `{ data: { attributes: { url } } }`). The TV app talks to
 * Strapi 5 now (web/mobile migrated in phase 27 + the phase-53 SDK); these two
 * helpers read BOTH shapes so the catalog renders against the flat response.
 * Mirrors packages/mobile/src/utils/parser.ts (the canonical reference).
 */
type StrapiRaw = Record<string, unknown>;

const getAttr = (item: StrapiRaw | undefined): StrapiRaw =>
  (item?.attributes as StrapiRaw) || item || {};

const getData = (item: StrapiRaw | undefined): StrapiRaw => {
  if (!item?.data) return item || {};
  const data = item.data as StrapiRaw;
  const attrs = data.attributes as StrapiRaw | undefined;
  return attrs ? { id: data.id, ...attrs } : data;
};

const profilesTitles: Record<string, string> = {
  male: 'Masculino',
  female: 'Feminino',
};

export const parseStrapiImage = (url: string | null | undefined) => {
  return url
    ? url?.indexOf('://') !== -1
      ? url
      : url?.indexOf(branding.cdnDomain) !== -1
        ? `https://${url}`
        : `${API_ENDPOINT.replace(/\/api$/, '')}${url}`
    : '';
};

export const parseProfile = (item: Record<string, unknown>) => {
  return {
    ...item,
    id: item.id,
    name: item.name,
    image: { uri: parseStrapiImage((item?.picture as Record<string, string>)?.url) },
    gender: { title: profilesTitles[item.gender as string], type: item.gender },
  };
};

export const parseProfileImage = (item: Record<string, unknown>) => {
  return {
    id: item.id,
    name: item.name,
    image: { uri: parseStrapiImage(item.url as string) },
  };
};

const categoriesOption: Record<string, { title: string; color: string }> = {
  Filme: {
    title: 'Filme',
    color: 'blue',
  },
  Série: {
    title: 'Série',
    color: 'yellow',
  },

  Musical: {
    title: 'Musical',
    color: 'blue',
  },
  Educativo: {
    title: 'Educativo',
    color: 'green',
  },
};

export const parseBanner = (item: StrapiRaw) => {
  // featured_show / featured_banner are flat relations in Strapi 5 (no
  // `.data.attributes`); getData/getAttr collapse v4 nesting AND pass v5 flat.
  const show = getData(item?.featured_show as StrapiRaw);
  const showAttrs = getAttr(show);
  const banner = getData(item?.featured_banner as StrapiRaw);
  const bannerAttrs = getAttr(banner);
  return {
    // Strapi 5 REST findOne resolves by documentId (numeric id 404s), so
    // navigation/ReadShowDetails must carry it. Fall back to the numeric id.
    id: (showAttrs?.documentId as string) || (show?.documentId as string) || (show?.id as number),
    title: showAttrs?.name,
    description: showAttrs?.description,
    // main hotfix: featured_banner ausente → cai pra bunny_image do show (capa
    // nunca fica vazia quando o banner dedicado não foi cadastrado).
    image: {
      uri: parseStrapiImage(
        (bannerAttrs?.url as string | undefined) || (showAttrs?.bunny_image as string | undefined),
      ),
    },
    category: categoriesOption['Filme'],
  };
};

interface IconDef {
  icon: string;
  width: number;
  height: number;
  fill: string;
}

const categoryIcons: Record<string, IconDef> = {
  Playlist: { icon: 'play', width: getScaledValue(9), height: getScaledValue(11), fill: '#ffffff' },
  Conteúdos: {
    icon: 'smile',
    width: getScaledValue(15),
    height: getScaledValue(17),
    fill: branding.colors.primary,
  },
  Educativos: {
    icon: 'paint',
    width: getScaledValue(14),
    height: getScaledValue(12),
    fill: branding.colors.primary,
  },
  Filmes: {
    icon: 'popcorn',
    width: getScaledValue(14),
    height: getScaledValue(12),
    fill: branding.colors.primary,
  },
  'Ver Mais': {
    icon: 'plus',
    width: getScaledValue(14),
    height: getScaledValue(12),
    fill: '#ffffff',
  },
};

export const parseCategory = (item: StrapiRaw) => {
  // Strapi 5 hoists name/icon to the entity root; getAttr/getData also accept
  // v4's `attributes` + `icon.data.attributes` nesting.
  const attrs = getAttr(item);
  const icon = getData(attrs?.icon as StrapiRaw);
  const iconAttrs = getAttr(icon);
  return {
    title: attrs?.name,
    id: item?.id,
    icon: categoryIcons[attrs?.name as string] || {},
    image: iconAttrs?.url ? parseStrapiImage(iconAttrs.url as string) : null,
  };
};

const collectionResources: Record<string, Record<string, unknown>> = {
  'Meus Favoritos': {
    icon: {
      icon: 'liked',
      width: getScaledValue(22),
      height: getScaledValue(20),
      fill: branding.colors.primary,
    },
  },
  'As 20 Músicas + Vistas': {
    icon: {
      icon: 'music',
      width: getScaledValue(17),
      height: getScaledValue(21),
      fill: branding.colors.primary,
    },
  },
  'Cante e Dance': {
    icon: {
      icon: 'music',
      width: getScaledValue(17),
      height: getScaledValue(21),
      fill: branding.colors.primary,
    },
  },
};

export const parseCollection = (item: StrapiRaw) => {
  const attrs = getAttr(item);
  const icon = getData(attrs?.icon as StrapiRaw);
  const iconAttrs = getAttr(icon);
  // Strapi 5 returns stream_shows as a flat array on the entity root; Strapi 4
  // wrapped it as `stream_shows.data`. Accept both.
  const streamShows = attrs?.stream_shows as StrapiRaw | undefined;
  const showsData =
    (streamShows?.data as StrapiRaw[]) || (attrs?.stream_shows as StrapiRaw[]) || [];
  return {
    id: item?.id,
    title: attrs?.name,
    ...(collectionResources[attrs?.name as string] || {}),
    videos: Array.isArray(showsData) && showsData.length ? showsData.map(parseVideo) : [],
    image: iconAttrs?.url ? parseStrapiImage(iconAttrs.url as string) : null,
  };
};

export const parseVideo = (item: StrapiRaw) => {
  const attrs = getAttr(item);
  const thumb = getData(attrs?.thumbnail as StrapiRaw);
  const thumbAttrs = getAttr(thumb);
  return {
    // documentId-first so navigation/ReadShowDetails resolves in Strapi 5
    // (findOne by documentId; numeric id 404s). Fall back to the numeric id.
    id: (attrs?.documentId as string) || (item?.documentId as string) || (item?.id as number),
    // getAttr already collapses both shapes (returns the flat item when there is
    // no `attributes` wrapper), so a separate `|| item?.name` fallback is dead.
    title: attrs?.name,
    description: attrs?.description,
    cover: {
      uri: parseStrapiImage(thumbAttrs?.url as string | undefined),
    },
  };
};

export const parseVideoShow = (item: StrapiRaw) => {
  const attrs = getAttr(item);
  const wide = getData(attrs?.wide_picture as StrapiRaw);
  const wideAttrs = getAttr(wide);
  const thumb = getData(attrs?.thumbnail as StrapiRaw);
  const thumbAttrs = getAttr(thumb);
  return {
    ...item,
    title: item.name,
    cover: {
      uri: parseStrapiImage(wideAttrs?.url as string | undefined),
    },
    thumb: {
      uri: parseStrapiImage(thumbAttrs?.url as string | undefined),
    },
  };
};
